import { useState } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, Eye, EyeOff, Loader2 } from "lucide-react";
import PageBlobs from "@/components/PageBlobs";
import GlowSpecks from "@/components/GlowSpecks";
import { apiFetch } from "@/api";

export const inputClass =
  "w-full rounded-2xl border border-[#E3ECE6] bg-[#F8FAF7] px-5 py-3.5 text-sm font-semibold text-[#17201A] outline-none transition-[border-color,background-color,box-shadow] duration-200 placeholder:font-normal placeholder:text-[#647067]/60 focus:border-[#16A34A] focus:bg-white focus:shadow-sm";

export const AuthLayout = ({ title, subtitle, children, footer }) => (
  <div className="relative flex min-h-screen flex-col overflow-hidden bg-[#F8FAF7] font-body text-[#17201A]">
    <PageBlobs />
    <header className="relative mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-6">
      <Link
        to="/"
        data-testid="auth-logo"
        className="font-display text-2xl font-extrabold"
      >
        <img src="/logo.png" alt="Abhyas" className="h-10 w-auto" />
      </Link>
    </header>
    <main className="relative flex flex-1 items-start justify-center px-6 pb-16 pt-6 sm:items-center sm:pt-0">
      <div className="relative w-full max-w-md">
        <GlowSpecks />
      <div
        data-testid="auth-card"
        className="animate-fade-up relative w-full max-w-md rounded-[2rem] border border-[#E3ECE6] bg-white p-8 shadow-sm sm:p-10"
      >
        <div className="orbit-ring" aria-hidden="true" />
        <div
          data-testid="auth-icon-badge"
          className="mb-5 inline-flex rounded-2xl bg-[#16A34A]/10 p-3 text-[#16A34A]"
        >
          <GraduationCap className="h-6 w-6" />
        </div>
        <h1
          data-testid="auth-title"
          className="font-display text-2xl font-extrabold sm:text-3xl"
        >
          {title}
        </h1>
        {subtitle && (
          <p data-testid="auth-subtitle" className="mt-2 text-sm text-[#17201A]/60">
            {subtitle}
          </p>
        )}
        <div className="mt-8">{children}</div>
        {footer && (
          <div className="mt-6 text-center text-sm text-[#17201A]/60">
            {footer}
          </div>
        )}
      </div>
      </div>
    </main>
  </div>
);


const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z"
    />
    <path
      fill="#34A853"
      d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z"
    />
    <path
      fill="#FBBC05"
      d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z"
    />
    <path
      fill="#EA4335"
      d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z"
    />
  </svg>
);

export const GoogleButton = () => {
  const [loading, setLoading] = useState(false);

  const startGoogleAuth = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const cfg = await apiFetch("/auth/google/config");
      if (cfg?.enabled && cfg.client_id) {
        // Custom Google OAuth — the app uses its own Google Cloud OAuth client,
        // so Google's consent screen is branded "Abhyas" on our own domain.
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const state = crypto.randomUUID();
        sessionStorage.setItem("google_oauth_state", state);
        const redirectUri = window.location.origin + "/auth/google/callback";
        const params = new URLSearchParams({
          client_id: cfg.client_id,
          redirect_uri: redirectUri,
          response_type: "code",
          scope: "openid email profile",
          state,
          prompt: "select_account",
        });
        window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
        return;
      }
    } catch {
      /* config endpoint unavailable — fall through to the managed flow */
    }
    // Emergent-managed Google Auth (used until the custom OAuth client is configured)
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
      redirectUrl
    )}`;
  };

  return (
    <button
      type="button"
      data-testid="google-auth-btn"
      onClick={startGoogleAuth}
      disabled={loading}
      className="flex w-full items-center justify-center gap-3 rounded-full border border-[#E3ECE6] bg-white py-3.5 text-sm font-bold text-[#17201A] shadow-sm transition-[background-color,border-color] duration-200 hover:border-[#16A34A]/40 hover:bg-[#F8FAF7] disabled:opacity-60"
    >
      {loading ? (
        <Loader2 className="h-5 w-5 animate-spin text-[#16A34A]" />
      ) : (
        <GoogleIcon />
      )}
      Continue with Google
    </button>
  );
};

export const AuthDivider = () => (
  <div className="flex items-center gap-3 py-1">
    <div className="h-px flex-1 bg-[#E3ECE6]" />
    <span className="text-xs font-semibold text-[#17201A]/45">
      Or continue with
    </span>
    <div className="h-px flex-1 bg-[#E3ECE6]" />
  </div>
);

export const PasswordField = ({
  label,
  testid,
  value,
  onChange,
  placeholder,
  error,
}) => {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="mb-1.5 block text-sm font-bold text-[#17201A]">
        {label}
      </label>
      <div className="relative">
        <input
          data-testid={testid}
          type={show ? "text" : "password"}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={`${inputClass} pr-12`}
        />
        <button
          type="button"
          data-testid={`${testid}-toggle`}
          onClick={() => setShow((s) => !s)}
          aria-label={show ? "Hide password" : "Show password"}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-[#647067] transition-colors duration-200 hover:text-[#16A34A]"
        >
          {show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
        </button>
      </div>
      {error && (
        <p
          data-testid={`${testid}-error`}
          className="mt-1.5 text-xs font-semibold text-red-500"
        >
          {error}
        </p>
      )}
    </div>
  );
};
